/**
 * <cart-shipping-calc-component> custom element
 *
 * Multi-item shipping cost calculator used inside the cart-drawer Versandkosten modal.
 * Fetches the current cart on every activation, country change and cart update.
 * Section morphs can preserve this instance, so a Liquid snapshot or instance
 * cache must not be used as the source of truth for shipping items.
 *
 * Required data attributes:
 *   data-currency     – ISO currency code, e.g. "EUR"
 *   data-locale       – BCP-47 locale for number formatting, e.g. "de-DE"
 *   data-shipping-label – Label prepended to the formatted price (e.g. "Versand")
 *
 * Children referenced by selector:
 *   [data-country-select]
 *   [data-loader]
 *   [data-result]
 *   [data-empty]
 */

import { fetchShippingRates } from './shipping-rates-api.js';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} ShippingItem
 * @property {string} name
 * @property {number} quantity
 * @property {number} grams
 * @property {number} price
 * @property {boolean} requires_shipping
 * @property {number} product_id
 * @property {number} variant_id
 */

class CartShippingCalcComponent extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {boolean} */
  #isActive = false;

  connectedCallback() {
    // Delegate to survive replacement of the select during section morphs.
    this.addEventListener('change', this.#onCountryChange);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    this.deactivate();
    this.removeEventListener('change', this.#onCountryChange);
    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);
  }

  /**
   * Called by the parent modal controller whenever the modal becomes visible.
   * Triggers a fresh rate calculation.
   */
  activate() {
    this.#isActive = true;
    this.#calculateShipping();
  }

  /** Called when the parent modal closes; cancels in-flight requests. */
  deactivate() {
    this.#isActive = false;
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /** @param {Event} event */
  #onCountryChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement) || !event.target.matches('[data-country-select]')) return;
    this.#calculateShipping();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.target instanceof HTMLSelectElement && event.target.matches('[data-country-select]') && event.key === 'Enter') {
      event.preventDefault();
    }
  };

  #onCartUpdate = () => {
    this.#calculateShipping();
  };

  // ─── Calculation ───────────────────────────────────────────────────────────

  /**
   * Query current output nodes: a cart section morph may have replaced them
   * while the cart or shipping request was in flight.
   * @param {'loading' | 'result' | 'empty' | 'error'} state
   * @param {string} [text]
   */
  #render(state, text = '') {
    const loader = /** @type {HTMLElement | null} */ (this.querySelector('[data-loader]'));
    const result = /** @type {HTMLElement | null} */ (this.querySelector('[data-result]'));
    const empty = /** @type {HTMLElement | null} */ (this.querySelector('[data-empty]'));

    if (loader) loader.hidden = state !== 'loading';
    if (empty) empty.hidden = state !== 'empty';
    if (result) {
      result.hidden = state !== 'result';
      result.textContent = text;
    }
  }

  async #calculateShipping() {
    if (!this.#isActive) return;

    // One cancellation scope for BOTH requests prevents older cart responses
    // from starting a rate calculation after a newer cart update or modal close.
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    const { signal } = controller;
    this.#render('loading');

    try {
      const res = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal,
      });
      if (!res.ok) throw new Error('Unable to refresh cart for shipping');
      const cart = await res.json();
      if (signal.aborted) return;
      if (!Array.isArray(cart?.items)) throw new Error('Invalid cart response');

      /** @type {ShippingItem[]} */
      const items = cart.items.map((/** @type {any} */ it) => ({
        name: it.product_title || it.title || '',
        quantity: Number(it.quantity) || 0,
        grams: Number(it.grams) || 0,
        price: Number(it.final_price ?? it.price ?? 0),
        requires_shipping: it.requires_shipping !== false,
        product_id: Number(it.product_id) || 0,
        variant_id: Number(it.variant_id) || 0,
      }));
      const shippableItems = items.filter((i) => i.requires_shipping && i.quantity > 0);
      if (shippableItems.length === 0) {
        this.#render('empty');
        return;
      }

      const select = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-country-select]'));
      const countryCode = select?.value || 'DE';
      const data = await fetchShippingRates(shippableItems, countryCode, signal);
      if (signal.aborted) return;

      const rates = Array.isArray(data?.rates) ? data.rates : [];
      const first = rates[0];
      if (!first) {
        this.#render('error');
        return;
      }

      const currency = this.dataset.currency || 'EUR';
      const locale = this.dataset.locale || 'de-DE';
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      });

      const cents = Number(first.total_price) || 0;
      const shippingLabel = this.dataset.shippingLabel || 'Versand';
      this.#render('result', `${shippingLabel} ${formatter.format(cents / 100)}`);
    } catch (_err) {
      if (signal.aborted) return;
      // Never quote a rate based on stale items when refreshing the cart fails.
      this.#render('error');
    } finally {
      if (this.#abortController === controller) this.#abortController = null;
    }
  }
}

if (!customElements.get('cart-shipping-calc-component')) {
  customElements.define('cart-shipping-calc-component', CartShippingCalcComponent);
}
